import React from 'react';
import type { PostureInstance } from './types';

type DriftKind = 'missing_baseline_admin' | 'missing_expected_member' | 'unexpected_member';
type DriftStatus = 'detected' | 'acknowledged' | 'resolved';

interface DriftRecord {
  driftId: string;
  instanceId: string;
  siteUrl?: string;
  kind: DriftKind;
  groupKey?: string;
  groupName?: string;
  detail: string;
  detectedAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  status: DriftStatus;
}

interface DriftHistoryEntry {
  historyId: string;
  driftId: string;
  at: string;
  event: 'detected' | 'acknowledged' | 'resolved';
  actor?: string;
  detail: string;
}

interface DriftScanState {
  tenantId: string;
  lastScanAt?: string;
  lastScanBy?: string;
  lastScanRecordCount?: number;
}

interface DriftSnapshot {
  records: DriftRecord[];
  history: DriftHistoryEntry[];
  scanState: DriftScanState;
}

interface DriftTabProps {
  tenantId: string;
  instances: PostureInstance[];
  onSwitchToApply?: (instanceId: string) => void;
}

const DRIFT_KIND_LABEL: Record<DriftKind, string> = {
  missing_baseline_admin: 'Missing baseline admin',
  missing_expected_member: 'Expected member missing',
  unexpected_member: 'Unexpected member present'
};

const prettyHost = (siteUrl?: string): string => {
  if (!siteUrl) return '(unknown site)';
  try {
    const parsed = new URL(siteUrl);
    return parsed.host + parsed.pathname;
  } catch {
    return siteUrl;
  }
};

const formatRelative = (iso?: string): string => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const seconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

const EMPTY_SNAPSHOT: DriftSnapshot = {
  records: [],
  history: [],
  scanState: { tenantId: '' }
};

export const DriftTab: React.FC<DriftTabProps> = ({ tenantId, instances, onSwitchToApply }) => {
  const [snapshot, setSnapshot] = React.useState<DriftSnapshot>(EMPTY_SNAPSHOT);
  const [loading, setLoading] = React.useState(false);
  const [scanning, setScanning] = React.useState(false);
  const [acking, setAcking] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async (): Promise<void> => {
    if (!tenantId) {
      setSnapshot(EMPTY_SNAPSHOT);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId });
      const response = await fetch(`/api/permissions/drift?${params.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to load drift (${response.status})`);
      }
      const body = await response.json() as DriftSnapshot;
      setSnapshot(body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const triggerScan = async (): Promise<void> => {
    if (!tenantId) return;
    setScanning(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/drift/scan', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Scan failed (${response.status})`);
      }
      const body = await response.json() as DriftSnapshot;
      setSnapshot(body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setScanning(false);
    }
  };

  const acknowledge = async (driftId: string): Promise<void> => {
    if (!tenantId) return;
    setAcking(driftId);
    setError(null);
    try {
      const response = await fetch(`/api/permissions/drift/${encodeURIComponent(driftId)}/acknowledge`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Acknowledge failed (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAcking(null);
    }
  };

  const fix = (record: DriftRecord): void => {
    if (onSwitchToApply) {
      onSwitchToApply(record.instanceId);
    }
  };

  const openRecords = snapshot.records.filter((record) => record.status !== 'resolved');
  const scanLabel = snapshot.scanState.lastScanAt
    ? `Last scan ${formatRelative(snapshot.scanState.lastScanAt)}${snapshot.scanState.lastScanBy ? ` by ${snapshot.scanState.lastScanBy}` : ''}`
    : 'No scan run yet for this tenant.';
  // Used to silence unused-prop warning when instances are not consumed directly; the data
  // pipeline runs server-side from posture, so this prop is now informational only.
  void instances;

  return (
    <div className="permissions-drift-tab">
      <div className="apply-header">
        <h3>Drift</h3>
        <p className="card-intro">
          Compares the published policy against each site's actual membership. Manual scan only — a
          scheduled background scan lands later.
        </p>
      </div>

      <section className="drift-scan-band">
        <div className="drift-scan-line">
          <span>{scanLabel}</span>
          {typeof snapshot.scanState.lastScanRecordCount === 'number' && (
            <>
              <span className="muted">·</span>
              <span>{snapshot.scanState.lastScanRecordCount} candidate{snapshot.scanState.lastScanRecordCount === 1 ? '' : 's'} on last scan</span>
            </>
          )}
        </div>
        <div className="drift-scan-actions">
          <button
            type="button"
            className="btn secondary"
            disabled={scanning || loading || !tenantId}
            onClick={() => void triggerScan()}
          >
            {scanning ? 'Scanning…' : 'Scan now'}
          </button>
        </div>
      </section>

      {error && <div className="permissions-error" role="alert">{error}</div>}

      <section className="apply-step">
        <h4>Open drift ({openRecords.length})</h4>
        {loading && openRecords.length === 0 && <span className="muted">Loading drift records…</span>}
        {!loading && openRecords.length === 0 && (
          <span className="muted">
            {snapshot.scanState.lastScanAt
              ? 'All sites are aligned with the published policy.'
              : 'No drift has been scanned yet. Click "Scan now" to compare each site to the policy.'}
          </span>
        )}
        {openRecords.length > 0 && (
          <ul className="drift-card-list">
            {openRecords.map((record) => (
              <li key={record.driftId} className={`drift-card status-${record.status}`}>
                <div className="drift-card-head">
                  <span className="drift-card-host">{prettyHost(record.siteUrl)}</span>
                  <span className="drift-card-kind">{DRIFT_KIND_LABEL[record.kind]}</span>
                  <span className="drift-card-when muted">first noticed {formatRelative(record.detectedAt)}</span>
                </div>
                <div className="drift-card-detail">{record.detail}</div>
                {record.status === 'acknowledged' && (
                  <div className="drift-card-ack muted">
                    Acknowledged by {record.acknowledgedBy ?? 'someone'} {formatRelative(record.acknowledgedAt)}
                  </div>
                )}
                <div className="drift-card-actions">
                  {record.status === 'detected' && (
                    <button
                      type="button"
                      className="btn secondary btn-small"
                      onClick={() => void acknowledge(record.driftId)}
                      disabled={acking === record.driftId}
                    >
                      {acking === record.driftId ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-small"
                    onClick={() => fix(record)}
                    title={onSwitchToApply ? 'Open the Apply tab scoped to this site' : 'Open the Apply tab to reconcile'}
                  >
                    Fix in Apply
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="apply-step apply-history-section">
        <h4>Drift history</h4>
        {snapshot.history.length === 0 ? (
          <span className="muted">No drift events recorded yet.</span>
        ) : (
          <ul className="drift-history-list">
            {snapshot.history.map((entry) => (
              <li key={entry.historyId} className={`drift-history-row event-${entry.event}`}>
                <span className="apply-history-when">{new Date(entry.at).toLocaleString()}</span>
                <span className={`drift-history-event event-${entry.event}`}>{entry.event}</span>
                <span className="drift-history-detail">{entry.detail}</span>
                {entry.actor && <span className="muted">— {entry.actor}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default DriftTab;
